import { isNumber } from 'lodash';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';
import { currencyFormat } from '../../../utils/formatHelper';
import {
  ORDER_TYPE_DROPSHIP,
  ORDER_TYPE_MARKETPLACE,
  ORDER_TYPE_REORDER,
  ORDER_TYPE_REPLENISH,
} from '../../../constants/orderTypes';

export function getCurrentTypes(retailerOrderType, orderType) {
  return {
    retailerOrderType,
    isDropshipRetailer: retailerOrderType === ORDER_TYPE_DROPSHIP,
    isMarketplaceRetailer: retailerOrderType === ORDER_TYPE_MARKETPLACE,
    isReorder: orderType === ORDER_TYPE_REPLENISH || orderType === ORDER_TYPE_REORDER,
  };
}

export function getOrderLinePrices(
  orderLine,
  { isDropshipRetailer, isMarketplaceRetailer, isReorder, retailerSupplier },
) {
  const {
    billed_amount: billedAmount,
    unit_price: unitPrice,
    unit_price_from_pricing_service: unitPriceFromPricingService,
  } = orderLine;

  if (isMarketplaceRetailer) {
    const retailPrice = billedAmount ?? 0;
    const commissionPercentage = retailerSupplier.operator_commission / 100;
    return {
      cost: NO_VALUE_PLACEHOLDER,
      lineTotal: retailPrice,
      retailPrice,
      commission: retailPrice * commissionPercentage,
    };
  }

  if (isDropshipRetailer) {
    const cost = unitPrice ?? unitPriceFromPricingService ?? 0;
    const retailPrice = isNumber(billedAmount) ? billedAmount : NO_VALUE_PLACEHOLDER;
    const lineTotal = isNumber(retailPrice) ? cost : retailPrice;
    return {
      cost,
      lineTotal,
      retailPrice,
      commission: NO_VALUE_PLACEHOLDER,
    };
  }

  if (isReorder) {
    const cost = unitPriceFromPricingService;
    return {
      cost,
      lineTotal: cost,
      commission: NO_VALUE_PLACEHOLDER,
      retailPrice: NO_VALUE_PLACEHOLDER,
    };
  }

  throw new Error('Unknown retailer order type');
}

export function getOrderSummaryTableColumns(rows, currency, shouldAddCommissionColumn) {
  const hasShipped = rows.some((row) => row.shipped > 0);
  const hasCancelled = rows.some((row) => row.cancelled > 0);
  const hasReturned = rows.some((row) => row.returned > 0);
  const hasTrackingNumber = rows.some((row) => row.tracking_number !== NO_VALUE_PLACEHOLDER);

  const columns = [
    {
      text: 'Seller',
      dataField: 'supplier_name',
    },
    {
      text: 'Title',
      dataField: 'variant.title',
    },
    {
      text: 'SKU',
      dataField: 'variant.sku',
    },
    {
      text: 'Retail Price',
      dataField: 'retail_price',
      formatter: (cell) => currencyFormat(cell, currency),
    },
    {
      text: 'Cost',
      dataField: 'cost',
      formatter: (cell) => (isNaN(cell) ? NO_VALUE_PLACEHOLDER : currencyFormat(cell, currency)),
    },
    {
      text: 'Ordered',
      dataField: 'original_quantity',
    },
  ];

  if (hasShipped) {
    columns.push({
      text: 'Shipped',
      dataField: 'shipped',
    });
  }

  if (hasCancelled) {
    columns.push({
      text: 'Cancelled',
      dataField: 'cancelled',
    });
  }

  if (hasReturned) {
    columns.push({
      text: 'Returned',
      dataField: 'returned',
    });
  }

  if (hasTrackingNumber) {
    columns.push(
      {
        text: 'Tracking Number',
        dataField: 'tracking_number',
      },
      {
        text: 'Carrier',
        dataField: 'carrier',
      },
    );
  }

  if (shouldAddCommissionColumn) {
    columns.push({
      text: 'Commission',
      dataField: 'commission',
      formatter: (cell) => (isNaN(cell) ? cell : currencyFormat(cell, currency)),
    });
  }

  columns.push({
    text: 'Line Total',
    dataField: 'line_total',
    formatter: (cell, row) => {
      const lineTotal = cell * row.quantity;
      if (!row.returned) return currencyFormat(lineTotal, currency);
      const totalWithoutReturned = cell * (row.quantity - row.returned);
      return formatWithStrikeThroughValue(lineTotal, totalWithoutReturned, row.currency);
    },
  });

  return columns;
}

function formatWithStrikeThroughValue(strikeThroughValue, value, currency) {
  return (
    <>
      <span className="cross">{currencyFormat(strikeThroughValue, currency)}</span>
      <span>{currencyFormat(value, currency)}</span>
    </>
  );
}

export function getTypesBySupplierIds({ retailerSuppliers, retailerOrderType }) {
  return retailerSuppliers.reduce((typesBySupplierId, retailerSupplier) => {
    const finalOrderType = retailerSupplier.order_type || retailerOrderType;
    typesBySupplierId[retailerSupplier.supplier.id] = {
      ...getCurrentTypes(finalOrderType),
      retailerSupplier,
    };
    return typesBySupplierId;
  }, {});
}
